import { cloneDeepWith } from "lodash-es";

import {
  FETCH_PAGES,
  MERGE_PAGES,
  UPDATE_PAGE,
  RESTORE_PAGES,
  OPEN_EDITOR,
  CLOSE_EDITOR,
  CHANGE_TITLE,
  CHANGE_TEACHING_NOTES,
  CHANGE_REMARK_TEACHING_NOTES,
  CHANGE_TEXT,
  CHANGE_REMARK_TEXT,
  CHANGE_AUDIO_HIGHLIGHT_ENABLED,
  CHANGE_CONTENT_TEXT,
  CHANGE_REMARK_CONTENT_TEXT,
  CHANGE_VIDEO,
  CHANGE_IMAGE,
  REMOVE_IMAGE,
  ENSURE_PAGE_DATA_SLOTS,
  DETACH_PDF_SOURCE,
  UPDATE_PDF_SOURCE,
  ADD_AUDIO,
  ADD_VIDEO,
  MOVE_AUDIO,
  CHANGE_AUDIO,
  REMOVE_AUDIO,
  DELETE_AUDIO,
  ADD_ARROW,
  MOVE_ARROW,
  DELETE_ARROW,
  UPDATE_ARROW,
  ADD_CARD,
  CHANGE_CARD_TEXT,
  CHANGE_CARD_REMARK_TEXT,
  CHANGE_CARD_IMAGE,
  CHANGE_CARD_AUDIO,
  REMOVE_VIDEO,
  CHANGE_VIDEO_STATUS,
  REMOVE_CARD_IMAGE,
  REMOVE_CARD_AUDIO,
  DELETE_CARD,
  CLEAN_AUTH,
  ADD_IMAGE,
  ADD_FILE,
  LOADING_PAGES,
  MOVE_CARD,
  FETCH_PAGES_NUMBER,
  ADD_PLAYGROUND,
  DELETE_PLAYGROUND,
  ADD_PLAYGROUNDLEVEL,
  CHANGE_INSTRUCTION_TEXT,
  CHANGE_INSTRUCTION_IMAGE,
  REMOVE_INSTRUCTION_IMAGE
} from "../actions/type";

// lodash cloneDeep silently turns File / Blob into an empty `{}` — the docs
// list them as "uncloneable values". Editor state holds pending-upload File
// objects in `_image.file` / `_audio.file` (set by ImageField / AudioField
// onChange, and by PdfSourcePanel when inserting an image from a PDF page),
// so a plain cloneDeep on ANY reducer action (e.g. dragging an audio slot →
// ADD_AUDIO, typing a title → CHANGE_TITLE) corrupts them into `{}`.
//
// The next save sees `_image.file = {}` (truthy), uploads an empty FormData,
// fails or stores a broken record — and the real image disappears.
//
// Fix once at the source: wrap cloneDeepWith with a customizer that returns
// the SAME reference for File/Blob (they're immutable binary blobs, so
// shared references across state snapshots are safe). Every cloneDeep call
// in this reducer now goes through this version.
const cloneDeep = (value) =>
  cloneDeepWith(value, (v) =>
    typeof Blob !== "undefined" && v instanceof Blob ? v : undefined
  );

const INIT_STATE = {
  editor: {},
  editorOriginal: null,
  pageMap: {},
  isEditing: false,
  isLoadingPages: false,
  pagesNumbers: {},
  // classHealthCheck mode "number": unit-wide answerable question total
  // reported by GET /api/page?sectionId (null when not in that mode)
  classHealthCheckTotalQuestions: null,
  // classHealthCheck draw modes: { unitId, ids } — the sections of that unit
  // the server will serve nothing for (GET /api/page?sectionId). Authoritative
  // over the client's cached unit settings; see utils/classHealthCheck.js
  // isSectionServingNothing. null when unknown.
  classHealthCheckEmptySections: null,
  // 簡誼健檢 "perSection": { [sectionId]: { unitId, pageIds } } — the order
  // the server drew each section's pages in (shuffled; a 題組 keeps its own
  // order). The student host walks sections in it — see
  // utils/classHealthCheck.js applyDrawOrder.
  classHealthCheckDrawOrder: {},
  // sectionId of the last completed section fetch (null when the fetch had
  // no sectionId). A completed fetch can legitimately load zero pages.
  fetchedSectionId: null,
  // Bumped whenever the page map is replaced (FETCH_PAGES, clearPages after a
  // submit): a background prefetch that started before drops its answer
  pageEpoch: 0
};

// { unitId, ids } from a GET /api/page?sectionId response, or null
const emptySectionsOf = (payload) => {
  const chc = payload?.classHealthCheck;
  return chc && chc.unitId && Array.isArray(chc.emptySectionIds)
    ? { unitId: String(chc.unitId), ids: chc.emptySectionIds.map(String) }
    : null;
};

// A section fetch's serving order (`classHealthCheck.pageOrder`, perSection
// only) replaces that section's entry; a fetch of the section without one
// (the unit left perSection) removes it, so the section's own order is back
const withDrawOrder = (orders, payload) => {
  const sectionId = payload?.sectionId;
  if (!sectionId) return orders;
  const chc = payload.classHealthCheck;
  const next = { ...orders };
  if (chc && chc.unitId && Array.isArray(chc.pageOrder)) {
    next[sectionId] = {
      unitId: String(chc.unitId),
      pageIds: chc.pageOrder.map(String)
    };
  } else {
    delete next[sectionId];
  }
  return next;
};

// Merge fetches: keep the last list unless this answer brings a new one —
// or is a section fetch of the SAME unit that brought none (the admin moved
// the unit to "all" meanwhile), which makes the kept list stale.
const mergedEmptySections = (state, payload) => {
  const fresh = emptySectionsOf(payload);
  if (fresh) return fresh;
  const kept = state.classHealthCheckEmptySections;
  if (
    kept &&
    payload?.sectionId &&
    payload?.contextUnitId &&
    String(payload.contextUnitId) === kept.unitId
  ) {
    return null;
  }
  return kept;
};

export default (state = INIT_STATE, { type, payload }) => {
  let newState;

  switch (type) {
    case FETCH_PAGES:
      return {
        ...state,
        pageMap: { ...payload.pageMap },
        isLoadingPages: false,
        classHealthCheckTotalQuestions:
          payload.classHealthCheck?.totalQuestions ?? null,
        classHealthCheckEmptySections: emptySectionsOf(payload),
        // a replacing fetch drops every earlier section's pages, and their
        // order with them (clearPages after a submit lands here too)
        classHealthCheckDrawOrder: withDrawOrder({}, payload),
        fetchedSectionId: payload.sectionId ?? null,
        pageEpoch: (state.pageEpoch || 0) + 1
      };

    case MERGE_PAGES:
      return {
        ...state,
        pageMap: { ...state.pageMap, ...payload.pageMap },
        // a background prefetch (pageActions prefetchSectionPages) of the
        // NEXT section says nothing about the fetch the page may be waiting
        // on: leave the loading flag and fetchedSectionId alone
        isLoadingPages: payload.prefetch ? state.isLoadingPages : false,
        // keep the previously reported total when this response has none
        classHealthCheckTotalQuestions:
          payload.classHealthCheck?.totalQuestions ??
          state.classHealthCheckTotalQuestions,
        classHealthCheckEmptySections: mergedEmptySections(state, payload),
        classHealthCheckDrawOrder: withDrawOrder(
          state.classHealthCheckDrawOrder || {},
          payload
        ),
        fetchedSectionId: payload.prefetch
          ? state.fetchedSectionId
          : (payload.sectionId ?? state.fetchedSectionId)
      };

    case LOADING_PAGES:
      // payload.isLoading === false resets the flag after a failed fetch so
      // pages that gate on isLoadingPages don't show "Loading..." forever
      return { ...state, isLoadingPages: payload?.isLoading !== false };

    case UPDATE_PAGE:
      return {
        ...state,
        pageMap: {
          ...state.pageMap,
          [payload.page._id]: payload.page
        },
        // When saving the currently edited page, advance editorOriginal to the
        // freshly saved server data so CLOSE_EDITOR (called right after save)
        // does not overwrite pageMap with the stale pre-edit data.
        editorOriginal:
          state.editor._id === payload.page._id
            ? payload.page
            : state.editorOriginal
      };

    case RESTORE_PAGES:
      newState = cloneDeep(state);
      payload.pageIds.forEach((pageId) => {
        newState.pageMap[pageId].isTrash = false;
      });
      return newState;

    /**
     *  EDITOR
     */

    case OPEN_EDITOR:
      return {
        ...state,
        editor: cloneDeep(state.pageMap[payload.pageId]),
        editorOriginal: state.pageMap[payload.pageId],
        isEditing: true
      };

    case CLOSE_EDITOR: {
      const original = state.editorOriginal;
      return {
        ...state,
        editor: {},
        editorOriginal: null,
        isEditing: false,
        pageMap: original
          ? { ...state.pageMap, [original._id]: original }
          : state.pageMap
      };
    }

    case CHANGE_TITLE:
      return {
        ...state,
        editor: { ...state.editor, title: payload.value }
      };

    case CHANGE_TEACHING_NOTES:
      return {
        ...state,
        editor: {
          ...state.editor,
          data: { ...state.editor.data, teachingNotes: payload.value }
        }
      };

    case CHANGE_REMARK_TEACHING_NOTES:
      return {
        ...state,
        editor: {
          ...state.editor,
          data: { ...state.editor.data, remarkTeachingNotes: payload.value }
        }
      };

    case CHANGE_TEXT:
      newState = cloneDeep(state);
      // Initialize texts array if it doesn't exist
      if (!newState.editor.data.texts) {
        newState.editor.data.texts = [];
      }
      if (newState.editor.data.texts[payload.index]) {
        newState.editor.data.texts[payload.index].value = payload.value;
      } else {
        newState.editor.data.texts[payload.index] = {
          value: payload.value,
          exist: true
        };
      }
      return newState;

    case CHANGE_REMARK_TEXT:
      newState = cloneDeep(state);

      if (!newState.editor.data.remarks) {
        newState.editor.data.remarks = [];
      }

      if (newState.editor.data.remarks[payload.index]) {
        newState.editor.data.remarks[payload.index].value = payload.value;
      } else {
        newState.editor.data.remarks[payload.index] = { value: payload.value };
      }
      return newState;

    case CHANGE_AUDIO_HIGHLIGHT_ENABLED:
      newState = cloneDeep(state);
      if (!newState.editor.data) newState.editor.data = {};
      newState.editor.data.audioHighlightEnabled = !!payload;
      return newState;

    case CHANGE_CONTENT_TEXT:
      newState = cloneDeep(state);
      // Initialize contentTexts array if it doesn't exist
      if (!newState.editor.data.contentTexts) {
        newState.editor.data.contentTexts = [];
      }
      if (newState.editor.data.contentTexts[payload.index]) {
        newState.editor.data.contentTexts[payload.index].value = payload.value;
      } else {
        newState.editor.data.contentTexts[payload.index] = {
          value: payload.value,
          exist: true
        };
      }
      return newState;

    case CHANGE_REMARK_CONTENT_TEXT:
      newState = cloneDeep(state);

      if (!newState.editor.data.contentRemarks) {
        newState.editor.data.contentRemarks = [];
      }

      if (newState.editor.data.contentRemarks[payload.index]) {
        newState.editor.data.contentRemarks[payload.index].value =
          payload.value;
      } else {
        newState.editor.data.contentRemarks[payload.index] = {
          value: payload.value
        };
      }
      return newState;

    case ADD_IMAGE:
      return {
        ...state,
        editor: {
          ...state.editor,
          data: {
            ...state.editor.data,
            images: [...state.editor.data.images, payload.image]
          }
        }
      };

    case ENSURE_PAGE_DATA_SLOTS: {
      // Pad texts / remarks / images / audios up to payload.slots[key]. Holes
      // are filled too (a sparse array would serialise as null entries).
      const fillers = {
        texts: () => ({ value: "", exist: true }),
        remarks: () => ({ value: "" }),
        images: () => ({ exist: true }),
        audios: () => ({ exist: true })
      };
      const slots = payload.slots || {};
      const needsPadding = Object.keys(slots).some((key) => {
        if (!fillers[key]) return false;
        const arr = state.editor.data[key];
        if (!Array.isArray(arr)) return true;
        if (arr.length < slots[key]) return true;
        for (let i = 0; i < slots[key]; i++) if (!arr[i]) return true;
        return false;
      });
      if (!needsPadding) return state;

      newState = cloneDeep(state);
      Object.keys(slots).forEach((key) => {
        if (!fillers[key]) return;
        const arr = Array.isArray(newState.editor.data[key])
          ? newState.editor.data[key]
          : [];
        for (let i = 0; i < slots[key]; i++) {
          if (!arr[i]) arr[i] = fillers[key]();
        }
        newState.editor.data[key] = arr;
      });
      return newState;
    }

    case CHANGE_IMAGE: {
      newState = cloneDeep(state);

      // Determine which array to update based on fieldType
      const targetArray =
        payload.fieldType === "contentImage" ? "contentImages" : "images";

      // Ensure target array exists
      if (!newState.editor.data[targetArray]) {
        newState.editor.data[targetArray] = [];
      }

      // payload._id, when present, references an already-persisted Image doc
      // (e.g. inserting from PdfSourcePanel). Without it, savePage drops the
      // image because it can't tell apart an attached-by-ref vs unsaved blob.
      const existingId =
        newState.editor.data[targetArray][payload.index]?._image?._id;
      const resolvedId = payload._id || existingId;

      if (newState.editor.data[targetArray][payload.index]) {
        newState.editor.data[targetArray][payload.index]._image = {
          ...newState.editor.data[targetArray][payload.index]._image,
          ...(resolvedId ? { _id: resolvedId } : {}),
          src: payload.src,
          name: payload.name,
          file: payload.file // Store File object for upload
        };
      } else {
        newState.editor.data[targetArray][payload.index] = {
          _image: {
            ...(resolvedId ? { _id: resolvedId } : {}),
            src: payload.src,
            name: payload.name,
            file: payload.file // Store File object for upload
          }
        };
      }

      // Sync to pageMap so template preview updates immediately
      const previewPageId = newState.editor._id;
      if (previewPageId && newState.pageMap[previewPageId]) {
        if (!newState.pageMap[previewPageId].data[targetArray]) {
          newState.pageMap[previewPageId].data[targetArray] = [];
        }
        if (newState.pageMap[previewPageId].data[targetArray][payload.index]) {
          newState.pageMap[previewPageId].data[targetArray][
            payload.index
          ]._image = {
            ...newState.pageMap[previewPageId].data[targetArray][payload.index]
              ._image,
            src: payload.src,
            name: payload.name
          };
        } else {
          newState.pageMap[previewPageId].data[targetArray][payload.index] = {
            _image: { src: payload.src, name: payload.name }
          };
        }
      }
      return newState;
    }

    case REMOVE_IMAGE: {
      newState = cloneDeep(state);
      const removeTargetArray =
        payload.fieldType === "contentImage" ? "contentImages" : "images";
      if (newState.editor.data[removeTargetArray]?.[payload.index])
        newState.editor.data[removeTargetArray][payload.index]._image = null;
      // Sync to pageMap for immediate preview
      const removePageId = newState.editor._id;
      if (
        removePageId &&
        newState.pageMap[removePageId]?.data[removeTargetArray]?.[payload.index]
      ) {
        newState.pageMap[removePageId].data[removeTargetArray][
          payload.index
        ]._image = null;
      }
      return newState;
    }

    case UPDATE_PDF_SOURCE: {
      // Merge payload into editor.data.pdfSource. Used by Re-extract to
      // patch extractedText without disturbing imageId / pdfFileId.
      newState = cloneDeep(state);
      if (newState.editor.data?.pdfSource && payload) {
        newState.editor.data.pdfSource = {
          ...newState.editor.data.pdfSource,
          ...payload
        };
      }
      return newState;
    }

    case DETACH_PDF_SOURCE: {
      // Only clear editor.data.pdfSource — the *staged* edit. The PdfSourcePanel
      // reads from editor state, so it disappears immediately. pageMap (the
      // saved snapshot) is intentionally left untouched: if the user cancels
      // edit mode without saving, the next OPEN_EDITOR re-clones from pageMap
      // and pdfSource comes back — matching the user's "no save = no commit"
      // expectation for every other field on the page.
      newState = cloneDeep(state);
      if (newState.editor.data) {
        newState.editor.data.pdfSource = null;
      }
      return newState;
    }

    // ===============================================================
    // INSTRUCTION
    case CHANGE_INSTRUCTION_TEXT:
      newState = cloneDeep(state);
      if (!newState.editor.data.instruction) {
        newState.editor.data.instruction = {};
      }
      newState.editor.data.instruction.text = payload.text;
      return newState;

    case CHANGE_INSTRUCTION_IMAGE: {
      newState = cloneDeep(state);
      if (!newState.editor.data.instruction) {
        newState.editor.data.instruction = {};
      }
      newState.editor.data.instruction.image = {
        _image: {
          src: payload.src,
          name: payload.name,
          file: payload.file
        }
      };
      // Sync to pageMap for immediate preview
      const instrPageId = newState.editor._id;
      if (instrPageId && newState.pageMap[instrPageId]) {
        if (!newState.pageMap[instrPageId].data.instruction) {
          newState.pageMap[instrPageId].data.instruction = {};
        }
        newState.pageMap[instrPageId].data.instruction.image = {
          _image: { src: payload.src, name: payload.name }
        };
      }
      return newState;
    }

    case REMOVE_INSTRUCTION_IMAGE: {
      newState = cloneDeep(state);
      if (newState.editor.data.instruction?.image) {
        newState.editor.data.instruction.image._image = null;
      }
      // Sync to pageMap for immediate preview
      const removeInstrPageId = newState.editor._id;
      if (
        removeInstrPageId &&
        newState.pageMap[removeInstrPageId]?.data.instruction?.image
      ) {
        newState.pageMap[removeInstrPageId].data.instruction.image._image =
          null;
      }
      return newState;
    }

    // ===============================================================
    case ADD_VIDEO:
      newState = cloneDeep(state);
      newState.editor.data.videos.push(payload.video);
      return newState;

    case CHANGE_VIDEO:
      newState = cloneDeep(state);

      if (newState.editor.data.videos[payload.index]) {
        newState.editor.data.videos[payload.index]._video = {
          ...newState.editor.data.videos[payload.index]._video,
          src: payload.src,
          name: payload.name
        };
      } else {
        newState.editor.data.videos[payload.index] = {
          _video: {
            src: payload.src,
            name: payload.name
          }
        };
      }
      return newState;

    case REMOVE_VIDEO:
      newState = cloneDeep(state);
      if (newState.editor.data.videos[payload.index])
        newState.editor.data.videos[payload.index]._video = null;

      return newState;

    case CHANGE_VIDEO_STATUS:
      newState = cloneDeep(state);

      newState.pageMap[payload.pageId].data.videos[
        payload.index
      ]._video.status = payload.video.status;

      newState.pageMap[payload.pageId].data.videos[
        payload.index
      ]._video._thumbnail = payload.video._thumbnail;

      return newState;

    // ==================================================================

    case ADD_AUDIO:
      newState = cloneDeep(state);
      newState.editor.data.audios.push(payload.audio);
      return newState;

    case CHANGE_AUDIO:
      newState = cloneDeep(state);
      if (newState.editor.data.audios[payload.index]) {
        newState.editor.data.audios[payload.index]._audio = {
          ...newState.editor.data.audios[payload.index]._audio,
          src: payload.src,
          name: payload.name,
          file: payload.file // Store File object for upload
        };
      } else {
        newState.editor.data.audios[payload.index] = {
          _audio: {
            src: payload.src,
            name: payload.name,
            file: payload.file // Store File object for upload
          }
        };
      }
      return newState;

    case MOVE_AUDIO:
      newState = cloneDeep(state);
      if (newState.editor.data.audios[payload.index]) {
        newState.editor.data.audios[payload.index].position = payload.position;
      } else {
        newState.editor.data.audios[payload.index] = {
          position: payload.position
        };
      }
      return newState;

    case REMOVE_AUDIO:
      newState = cloneDeep(state);
      if (newState.editor.data.audios[payload.index])
        newState.editor.data.audios[payload.index]._audio = null;
      return newState;

    case DELETE_AUDIO:
      newState = cloneDeep(state);
      newState.editor.data.audios[payload.index] = null;
      return newState;

    // ==================================================================
    // ARROW ACTIONS
    // ==================================================================

    case ADD_ARROW:
      newState = cloneDeep(state);
      if (!newState.editor.data.arrows) {
        newState.editor.data.arrows = [];
      }
      newState.editor.data.arrows.push(payload.arrow);
      return newState;

    case MOVE_ARROW:
      newState = cloneDeep(state);
      if (!newState.editor.data.arrows) {
        newState.editor.data.arrows = [];
      }
      if (newState.editor.data.arrows[payload.index]) {
        newState.editor.data.arrows[payload.index].startX = payload.startX;
        newState.editor.data.arrows[payload.index].startY = payload.startY;
        newState.editor.data.arrows[payload.index].endX = payload.endX;
        newState.editor.data.arrows[payload.index].endY = payload.endY;
      }
      return newState;

    case DELETE_ARROW:
      newState = cloneDeep(state);
      if (newState.editor.data.arrows) {
        newState.editor.data.arrows[payload.index] = null;
      }
      return newState;

    case UPDATE_ARROW:
      newState = cloneDeep(state);
      if (!newState.editor.data.arrows) {
        newState.editor.data.arrows = [];
      }
      if (newState.editor.data.arrows[payload.index]) {
        newState.editor.data.arrows[payload.index] = {
          ...newState.editor.data.arrows[payload.index],
          ...payload.properties
        };
      }
      return newState;

    case ADD_CARD:
      newState = cloneDeep(state);
      newState.editor.data.cards.push(payload.card);
      return newState;

    case MOVE_CARD:
      newState = cloneDeep(state);
      if (newState.editor.data.cards[payload.index]) {
        newState.editor.data.cards[payload.index].position = payload.position;
      } else {
        newState.editor.data.cards[payload.index] = {
          position: payload.position,
          texts: [],
          images: [],
          audios: []
        };
      }
      return newState;

    case CHANGE_CARD_TEXT:
      newState = cloneDeep(state);
      if (payload.subIndex !== undefined) {
        if (newState.editor.data.cards[payload.index]) {
          if (newState.editor.data.cards[payload.index].texts) {
            if (
              newState.editor.data.cards[payload.index].texts[payload.subIndex]
            ) {
              newState.editor.data.cards[payload.index].texts[
                payload.subIndex
              ].value = payload.value;
            } else {
              newState.editor.data.cards[payload.index].texts[
                payload.subIndex
              ] = { value: payload.value };
            }
          } else {
            newState.editor.data.cards[payload.index].texts = [];
            newState.editor.data.cards[payload.index].texts[payload.subIndex] =
              { value: payload.value };
          }
        } else {
          newState.editor.data.cards[payload.index] = {
            texts: [],
            images: [],
            audios: []
          };
          newState.editor.data.cards[payload.index].texts[payload.subIndex] = {
            value: payload.value
          };
        }
      } else {
        if (newState.editor.data.cards[payload.index]) {
          if (newState.editor.data.cards[payload.index].text) {
            newState.editor.data.cards[payload.index].text.value =
              payload.value;
          } else {
            newState.editor.data.cards[payload.index].text = {
              value: payload.value
            };
          }
        } else {
          newState.editor.data.cards[payload.index] = {
            text: payload.value,
            texts: [],
            images: [],
            audios: []
          };
        }
      }
      return newState;

    case CHANGE_CARD_REMARK_TEXT:
      newState = cloneDeep(state);
      if (payload.subIndex !== undefined) {
        // card remarks
        if (newState.editor.data.cards[payload.index]) {
          if (newState.editor.data.cards[payload.index].remarks) {
            if (
              newState.editor.data.cards[payload.index].remarks[
                payload.subIndex
              ]
            ) {
              newState.editor.data.cards[payload.index].remarks[
                payload.subIndex
              ].value = payload.value;
            } else {
              newState.editor.data.cards[payload.index].remarks[
                payload.subIndex
              ] = { value: payload.value };
            }
          } else {
            newState.editor.data.cards[payload.index].remarks = [];
            newState.editor.data.cards[payload.index].remarks[
              payload.subIndex
            ] = { value: payload.value };
          }
        } else {
          newState.editor.data.cards[payload.index] = {
            remarks: [],
            texts: [],
            images: [],
            audios: []
          };
          newState.editor.data.cards[payload.index].remarks[payload.subIndex] =
            {
              value: payload.value
            };
        }
      } else {
        // card remark
        if (newState.editor.data.cards[payload.index]) {
          if (newState.editor.data.cards[payload.index].remark) {
            newState.editor.data.cards[payload.index].remark.value =
              payload.value;
          } else {
            newState.editor.data.cards[payload.index].remark = {
              value: payload.value
            };
          }
        } else {
          newState.editor.data.cards[payload.index] = {
            remark: payload.value,
            remarks: [],
            text: "",
            texts: [],
            images: [],
            audios: []
          };
        }
      }
      return newState;

    case CHANGE_CARD_IMAGE:
      newState = cloneDeep(state);
      if (payload.subIndex !== undefined) {
        // payload._id, when present, references an already-persisted Image
        // doc (e.g. inserting from PdfSourcePanel into a cards.images slot).
        // Without it, savePage would store an _image with no _id → server
        // keeps a dangling ref → image renders blank.
        // Mirrors the same payload._id contract on the no-subIndex branch
        // below and on CHANGE_IMAGE.
        const subExistingId =
          newState.editor.data.cards[payload.index]?.images?.[payload.subIndex]
            ?._image?._id;
        const subResolvedId = payload._id || subExistingId;
        const subImageObj = {
          ...(subResolvedId ? { _id: subResolvedId } : {}),
          src: payload.src,
          name: payload.name,
          file: payload.file
        };
        if (newState.editor.data.cards[payload.index]) {
          if (newState.editor.data.cards[payload.index].images) {
            if (
              newState.editor.data.cards[payload.index].images[payload.subIndex]
            ) {
              newState.editor.data.cards[payload.index].images[
                payload.subIndex
              ]._image = {
                ...newState.editor.data.cards[payload.index].images[
                  payload.subIndex
                ]._image,
                ...subImageObj
              };
            } else {
              newState.editor.data.cards[payload.index].images[
                payload.subIndex
              ] = {
                _image: subImageObj
              };
            }
          } else {
            newState.editor.data.cards[payload.index].images = [];
            newState.editor.data.cards[payload.index].images[payload.subIndex] =
              {
                _image: subImageObj
              };
          }
        } else {
          newState.editor.data.cards[payload.index] = {
            texts: [],
            images: [],
            audios: []
          };
          newState.editor.data.cards[payload.index].images[payload.subIndex] = {
            _image: subImageObj
          };
        }
      } else {
        // payload._id, when present, references an already-persisted Image
        // doc (e.g. inserting from PdfSourcePanel). Without it, savePage
        // would store an _image with no _id → server keeps a dangling ref.
        // Mirrors the same payload._id contract on CHANGE_IMAGE.
        if (newState.editor.data.cards[payload.index]) {
          const existingId =
            newState.editor.data.cards[payload.index].image?._image?._id;
          const resolvedId = payload._id || existingId;
          if (newState.editor.data.cards[payload.index].image) {
            newState.editor.data.cards[payload.index].image._image = {
              ...newState.editor.data.cards[payload.index].image._image,
              ...(resolvedId ? { _id: resolvedId } : {}),
              src: payload.src,
              name: payload.name
            };
          } else {
            newState.editor.data.cards[payload.index].image = {
              _image: {
                ...(resolvedId ? { _id: resolvedId } : {}),
                src: payload.src,
                name: payload.name
              }
            };
          }
        } else {
          newState.editor.data.cards[payload.index] = {
            image: {
              _image: {
                ...(payload._id ? { _id: payload._id } : {}),
                src: payload.src,
                name: payload.name
              }
            },
            texts: [],
            images: [],
            audios: []
          };
        }
      }
      return newState;

    case CHANGE_CARD_AUDIO:
      newState = cloneDeep(state);
      if (payload.subIndex !== undefined) {
        if (newState.editor.data.cards[payload.index]) {
          if (newState.editor.data.cards[payload.index].audios) {
            if (
              newState.editor.data.cards[payload.index].audios[payload.subIndex]
            ) {
              newState.editor.data.cards[payload.index].audios[
                payload.subIndex
              ]._audio = {
                ...newState.editor.data.cards[payload.index].audios[
                  payload.subIndex
                ]._audio,
                src: payload.src,
                name: payload.name
              };
            } else {
              newState.editor.data.cards[payload.index].audios[
                payload.subIndex
              ] = {
                _audio: {
                  src: payload.src,
                  name: payload.name
                }
              };
            }
          } else {
            newState.editor.data.cards[payload.index].audios = [];
            newState.editor.data.cards[payload.index].audios[payload.subIndex] =
              {
                _audio: {
                  src: payload.src,
                  name: payload.name
                }
              };
          }
        } else {
          newState.editor.data.cards[payload.index] = {
            texts: [],
            images: [],
            audios: []
          };
          newState.editor.data.cards[payload.index].audios[payload.subIndex] = {
            _audio: {
              src: payload.src,
              name: payload.name
            }
          };
        }
      } else {
        if (newState.editor.data.cards[payload.index]) {
          if (newState.editor.data.cards[payload.index].audio) {
            newState.editor.data.cards[payload.index].audio._audio = {
              ...newState.editor.data.cards[payload.index].audio._audio,
              src: payload.src,
              name: payload.name
            };
          } else {
            newState.editor.data.cards[payload.index].audio = {
              _audio: {
                src: payload.src,
                name: payload.name
              }
            };
          }
        } else {
          newState.editor.data.cards[payload.index] = {
            audio: {
              _audio: {
                src: payload.src,
                name: payload.name
              }
            },
            texts: [],
            images: [],
            audios: []
          };
        }
      }
      return newState;

    case REMOVE_CARD_IMAGE:
      newState = cloneDeep(state);
      if (payload.subIndex !== undefined) {
        if (
          newState.editor.data.cards[payload.index] &&
          newState.editor.data.cards[payload.index].images &&
          newState.editor.data.cards[payload.index].images[payload.subIndex]
        )
          newState.editor.data.cards[payload.index].images[
            payload.subIndex
          ]._image = null;
      } else {
        if (
          newState.editor.data.cards[payload.index] &&
          newState.editor.data.cards[payload.index].image
        )
          newState.editor.data.cards[payload.index].image._image = null;
      }
      return newState;

    case REMOVE_CARD_AUDIO:
      newState = cloneDeep(state);

      if (payload.subIndex !== undefined) {
        if (
          newState.editor.data.cards[payload.index] &&
          newState.editor.data.cards[payload.index].audios &&
          newState.editor.data.cards[payload.index].audios[payload.subIndex]
        )
          newState.editor.data.cards[payload.index].audios[
            payload.subIndex
          ]._audio = null;
      } else {
        if (
          newState.editor.data.cards[payload.index] &&
          newState.editor.data.cards[payload.index].audio
        )
          newState.editor.data.cards[payload.index].audio._audio = null;
      }
      return newState;

    case DELETE_CARD:
      newState = cloneDeep(state);
      newState.editor.data.cards[payload.index] = null;
      return newState;

    case ADD_FILE:
      return {
        ...state,
        editor: {
          ...state.editor,
          data: {
            ...state.editor.data,
            _files: [...state.editor.data._files, payload.file]
          }
        }
      };

    case FETCH_PAGES_NUMBER:
      return {
        ...state,
        pagesNumbers: payload
      };

    case CLEAN_AUTH:
      return { ...INIT_STATE };

    case ADD_PLAYGROUND:
      newState = cloneDeep(state);

      newState.editor.data.playgrounds = payload;

      return newState;

    case DELETE_PLAYGROUND:
      newState = cloneDeep(state);

      newState.editor.data.playgrounds.splice(payload.index, 1);

      return newState;

    case ADD_PLAYGROUNDLEVEL:
      newState = cloneDeep(state);

      newState.editor.data.playgroundLevel = payload;

      return newState;

    default:
      return state;
  }
};
